import {ChangeEvent, useCallback, useEffect, useMemo, useState} from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  DialogActions,
  Typography,
  List,
  ListItem,
  IconButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Alert,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {Upload} from '@mui/icons-material';
import {DialogClose} from '../../../../../components/dialogs/DialogFrame/DialogClose';
import MusicPreviewBtn from '../../../../../components/music/MusicPreviewBtn';
import {VisuallyHiddenInput} from '../../../../admin/global-content/generic-stingers-list/GenericStingersAddTracksModal';
import {IMediaTrack} from '../../../../../dto/MediaTrack.dto';
import AudioDurationValidation, {
  AudioSizeValidation,
  getAudioDurationFromFile,
} from '../../../../../components/utlis/Audio/GetAudioDuration';
import SubmitButton from '../../../../../components/styles/buttons/SubmitButton';
import {validateMp3File} from '../../../../../utils/validateMusicFile';

type Props = {
  type: string;
  label?: string;
  companyId?: number;
  onClose?: (track?: IMediaTrack) => void;
  setTrack?: (track: IMediaTrack | null) => void;
  isDisabled?: boolean;
};

const ProcessedFileUploadButton = ({
  label = 'Upload Processed File',
  type,
  companyId,
  onClose,
  setTrack,
  isDisabled = false,
}: Props) => {
  const [open, setOpen] = useState(false);
  const handleClose = useCallback(
    (track?: IMediaTrack) => {
      if (onClose) onClose(track);
      if (setTrack) setTrack(track ?? null);
      setOpen(false);
    },
    [onClose, setTrack],
  );
  return (
    <>
      <Button
        disabled={isDisabled}
        variant='contained'
        className='ml-2'
        startIcon={<Upload />}
        onClick={() => setOpen(true)}>
        {label}
      </Button>
      {open && (
        <UploadProcessedFileDialog
          type={type}
          companyId={companyId}
          onClose={handleClose}
        />
      )}
    </>
  );
};

export default ProcessedFileUploadButton;

export const UploadProcessedFileDialog = ({
  type,
  companyId,
  onClose,
}: Props) => {
  const [file, setFile] = useState<null | File>(null);
  const [url, setUrl] = useState('');
  const [fileDuration, setFileDuration] = useState(0);
  const [fileError, setFileError] = useState('');

  const handleClose = useCallback(() => {
    if (onClose) onClose();
  }, [onClose]);

  const handleFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.files?.length) {
        const newFile = event.target.files[0] as File;

        const {isValid, error} = validateMp3File(newFile);

        if (!isValid) {
          setFileError(error ?? '');
          event.target.value = '';
          return;
        }
        setFileError('');

        setFile(newFile);
        setFileDuration(await getAudioDurationFromFile(newFile));
      }
    },
    [],
  );

  const onSuccess = useCallback(
    response => {
      onClose?.(response.data.media_track);
    },
    [onClose],
  );

  const payload = useMemo(() => {
    const fd = new FormData();
    if (file) fd.append('file', file);
    fd.append('type', type);
    if (companyId) fd.append('company_id', companyId.toString());
    return fd;
  }, [companyId, file, type]);

  const handleDelete = useCallback(() => {
    setFile(null);
    setFileError('');
  }, []);

  useEffect(() => {
    setUrl(file ? URL.createObjectURL(file) : '');
  }, [file]);

  return (
    <Dialog open maxWidth='sm' fullWidth onClose={handleClose}>
      <DialogTitle>
        <Typography>Please choose the processed file</Typography>
        <DialogClose onClose={handleClose} />
      </DialogTitle>
      <DialogContent>
        <Stack>
          {fileError && (
            <Alert severity='error' sx={{my: 1}}>
              {fileError}
            </Alert>
          )}
          {file && (
            <>
              <AudioDurationValidation
                durationInSec={fileDuration}
                maxDurationInMin={5}
              />
              <AudioSizeValidation maxSizeInMb={10} fileSize={file.size} />
            </>
          )}
        </Stack>
        <Stack direction={'row'} justifyContent={'center'}>
          <Button
            component='label'
            variant='outlined'
            role={undefined}
            className='h-30'
            size='medium'
            disabled={Boolean(file)}
            startIcon={<CloudUploadIcon />}>
            Choose File
            <VisuallyHiddenInput
              type='file'
              accept='.mp3,audio/mpeg'
              onChange={handleFileChange}
              aria-label={'Choose processed audio file to upload'}
            />
          </Button>
        </Stack>

        {file && url && (
          <List sx={{mb: 2}}>
            <ListItem
              sx={{p: 2, background: '#0d1525'}}
              className='cursor-pointer'
              secondaryAction={
                <IconButton onClick={handleDelete} className='text-error'>
                  <DeleteOutlineIcon />
                </IconButton>
              }>
              <ListItemIcon>
                <MusicPreviewBtn
                  id={file.name}
                  url={url}
                  track_name={file.name}
                  track_artist={file.name}
                />
              </ListItemIcon>
              <ListItemText primary={file.name} />
            </ListItem>
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <SubmitButton
          payload={payload}
          title='Upload & Proceed'
          url={`/v1/media-track/upload`}
          successAction={onSuccess}
          buttonProps={{
            variant: 'contained',
            disabled:
              !file || fileDuration / 60 >= 5 || file.size / (1000 * 1000) > 10,
          }}
        />
        <Button variant='outlined' onClick={handleClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
